import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Radio, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const POLL_MS = 60 * 1000;

export default function LivestreamBanner({ config }) {
  const [dismissed, setDismissed] = useState(false);
  const [autoLive, setAutoLive] = useState(null); // Authoritative BBC live status.

  useEffect(() => {
    setDismissed(false);
  }, [config?.url, config?.text]);

  useEffect(() => {
    if (autoLive?.video_id) setDismissed(false);
  }, [autoLive?.video_id]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/livestream/status");
        if (!cancelled) setAutoLive(data);
      } catch { if (!cancelled) setAutoLive({ is_live: false, state: "UNKNOWN" }); }
    };
    check();
    const id = setInterval(check, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  const detectedLive = autoLive?.is_live === true;
  if (!detectedLive || dismissed) return null;

  const text = config?.text || "Live now";

  return (
    <AnimatePresence>
      <motion.div
        data-testid="livestream-banner"
        data-autodetect={detectedLive ? "true" : "false"}
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed top-0 left-0 right-0 z-[60] flex items-center bg-[#2C4C3B] text-[#FAF9F6] text-sm"
      >
        <Link
          to="/live"
          state={{ title: text }}
          aria-label={`LIVE — ${text}. Watch inside BBC Connect`}
          className="flex-1 flex items-center justify-center gap-3 px-4 py-2 min-w-0 hover:bg-[#1A3326] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#D4A373] transition-colors"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4A373] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D4A373]" />
          </span>
          <Radio className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span className="font-medium truncate">{text}</span>
          <span className="hidden sm:inline text-[#D4A373]">— Watch Now</span>
          <ArrowRight className="w-4 h-4 shrink-0" aria-hidden="true" />
        </Link>
        <button
          type="button"
          data-testid="livestream-dismiss"
          onClick={() => setDismissed(true)}
          className="mr-2 p-2 hover:bg-white/10 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#D4A373]"
          aria-label="Dismiss live banner"
        >
          <X className="w-4 h-4" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
